/**
 * Zero-state for a fresh dashboard workspace tab (S21) — REPLACES S2's
 * minimal "Create your first dashboard" card. Asks "What do you want to
 * watch?": Health / Storage / Activity are scoped to a picked connection and
 * pre-flighted (`services/starterPreflight.ts`) so only tiles whose preview
 * actually succeeds get created; Blank skips the preflight entirely and
 * opens the Add Tile dialog immediately (owned by the parent — that dialog
 * only mounts once a dashboard exists). Action logic lives in
 * `hooks/useStarterDashboardActions.ts`.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { useConnectionsUIStore } from '../../../connections/store/uiStore';
import { fetchServerVersionInfo } from '../../../connections/services/serverInfoService';
import { ConnectionSelector } from '../../common/components/ConnectionSelector';
import type { DashboardEngineId } from '../types/dashboardTypes';
import { templateCapabilities } from '../templates/templateCapabilities';
import { useStarterDashboardActions } from '../hooks/useStarterDashboardActions';
import { StarterPackPicker } from './StarterPackPicker';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

const KNOWN_ENGINES: ReadonlySet<string> = new Set([
  'mssql',
  'pgsql',
  'mysql',
  'oracle',
  'sqlite',
  'kusto',
]);
const FIFTEEN_MINUTES = 15 * 60 * 1000;

export function StarterDashboards({
  onDashboardCreated,
}: {
  onDashboardCreated: (dashboardId: string, opts?: { openAddTile?: boolean }) => void;
}) {
  const { track } = useAnalytics();
  const activeConnections = useConnectionStore((s) => s.activeConnections);
  const connectedConnections = useMemo(
    () => activeConnections.filter((c) => c.status === 'connected'),
    [activeConnections],
  );
  const [connectionId, setConnectionId] = useState('');

  // Default to the first connected connection — one less step for the
  // common single-connection case; the selector can still override.
  useEffect(() => {
    if (connectionId || connectedConnections.length === 0) return;
    setConnectionId(connectedConnections[0].id);
  }, [connectionId, connectedConnections]);

  const active = activeConnections.find((c) => c.id === connectionId);
  const engineRaw = active?.connection.engine;
  const engine: DashboardEngineId | undefined = KNOWN_ENGINES.has(engineRaw ?? '')
    ? (engineRaw as DashboardEngineId)
    : undefined;
  const database = active?.connection.database;

  // Live server version — same probe `TemplatesPane` feeds `availableTemplates`.
  const versionQuery = useQuery<string | undefined>({
    queryKey: ['dashboard-starter-server-version', connectionId, engine, database],
    queryFn: async () => {
      if (!connectionId || !engine) return undefined;
      try {
        const info = await fetchServerVersionInfo(connectionId, engine, database);
        return info.version;
      } catch {
        return undefined;
      }
    },
    enabled: Boolean(connectionId && engine && engine !== 'kusto'),
    staleTime: FIFTEEN_MINUTES,
  });
  const capabilities = useMemo(
    () => templateCapabilities(connectionId, engine, versionQuery.data),
    [connectionId, engine, versionQuery.data],
  );

  const { running, error, runPack, createBlank } = useStarterDashboardActions({
    connectionId,
    engine,
    database,
    serverVersion: versionQuery.data,
    capabilities,
    onDashboardCreated,
  });

  const packDisabledReason = !connectionId
    ? 'Pick a connection first'
    : !engine
      ? 'This connection type is not supported yet'
      : running !== null
        ? 'A pack is already loading — please wait'
        : null;

  return (
    <div
      // `h-full` alongside `flex-1` pins this to the parent tab's full height
      // even if an intermediate flex ancestor doesn't cleanly hand down a
      // definite height — belt-and-braces so the centered group can't end up
      // pinned to the top with a dead band below it.
      className="flex h-full flex-1 items-center justify-center overflow-y-auto p-8"
      data-testid="dashboard-zero-state"
    >
      <div className="flex w-full max-w-xl flex-col items-center gap-5 text-center">
        <div>
          <h2 className="text-base font-semibold text-foreground">What do you want to watch?</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Pick a connection and a starting point — we only add tiles that actually run.
          </p>
        </div>

        <div
          className="flex w-full max-w-xs items-center gap-2 rounded-md border border-border px-2 py-1.5"
          data-testid="starter-connection-select"
        >
          <span className="shrink-0 text-xs text-muted-foreground">Connection</span>
          <div className="min-w-0 flex-1">
            <ConnectionSelector
              connectionId={connectionId || undefined}
              connectionName={active?.connection.alias ? safeConnectionAlias(active.connection) : undefined}
              serverHost={safeConnectionServer(active?.connection)}
              onConnectionChange={(id) => setConnectionId(id)}
              showAllEngines
            />
          </div>
        </div>
        {connectedConnections.length === 0 && (
          <div className="flex flex-col items-center gap-2">
            <p className="text-xs text-muted-foreground">
              Connect to a database to get Health, Storage or Activity suggestions — or start blank.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 gap-1.5"
              onClick={() => {
                track('feature:dashboard_starter_add_connection');
                useConnectionsUIStore.getState().setShowNewConnectionDialog(true);
              }}
              data-testid="starter-add-connection-button"
            >
              <Plus className="h-3.5 w-3.5" />
              Add connection
            </Button>
          </div>
        )}

        <StarterPackPicker
          disabledReason={packDisabledReason}
          running={running}
          onPick={(pack) => void runPack(pack)}
          onBlank={() => void createBlank()}
        />

        {error && (
          <p className="text-xs text-destructive" data-testid="starter-error">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
